import { useState } from "react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import type { DeleteProjectModalProps } from "./deleteProjectModal.types";

export function DeleteProjectModal({
  isOpen,
  onClose,
  onConfirm,
  projectName,
  isDeleting,
}: DeleteProjectModalProps) {
  const [confirmText, setConfirmText] = useState("");
  const isConfirmValid = confirmText === projectName;

  const handleConfirm = () => {
    if (isConfirmValid) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmText("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-lg max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mr-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-raro-gray-900 font-montserrat">
                Deletar Projeto
              </h3>
              <p className="text-sm text-raro-gray-600">
                Esta ação não pode ser desfeita
              </p>
            </div>
          </div>
          
          {!isDeleting && (
            <button
              onClick={handleClose}
              className="p-2 text-raro-gray-400 hover:text-raro-gray-600 rounded-lg hover:bg-raro-gray-50"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-raro-gray-700 mb-4">
            Você está prestes a deletar permanentemente o projeto{" "}
            <span className="font-semibold text-raro-gray-900">"{projectName}"</span>.
          </p>
          
          <p className="text-sm text-raro-gray-600 mb-4">
            Para confirmar, digite o nome do projeto abaixo:
          </p>
          
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={projectName}
            disabled={isDeleting}
            className="w-full px-4 py-3 border border-raro-gray-300 rounded-xl font-montserrat focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          />
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="flex-1 px-4 py-3 text-raro-gray-700 bg-raro-gray-100 hover:bg-raro-gray-200 font-semibold font-montserrat rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={!isConfirmValid || isDeleting}
            className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold font-montserrat rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Deletando...
              </>
            ) : (
              "Deletar Projeto"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}